import React, { useEffect, useState, useMemo } from "react";
import { Formik, Form, Field } from "formik";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { getUserForUI } from "../../store/reducer/user/userSlice";
import { getOpportunityById } from "../../store/reducer/opportunity/opportunitySlice";
import {
  getDrawingById,
  updateDrawing,
  getDrawingLockStatus
} from "../../store/reducer/drawing/drawingSlice";
import { proposalStatusByDrawingId } from "../../store/reducer/drawing/drawingProposalSlice";
import {
  addFile,
  deleteFile,
  getAllFiles,
  getProposalFinalStatus
} from "../../store/reducer/drawing/drawingFileSilce";
import Notes from "../Notes/Notes";
import NotesTable from "../Drawing/DrawingNotesTable";
import DrawingTabs from "./DrawingTabs";

function EditDrawing() {
  const { id, drawing_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { options = [] } = useSelector((state) => state.ddOptions);

  const [opportunityData, setOpportunityData] = useState([]);
  const [drawingData, setDrawingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState([]);
  const [activeTab, setActiveTab] = useState(1);
  const [proposalStatus, setProposalStatus] = useState(true);
  const [isProposalFinalised, setIsProposalFinalised] = useState(false);
  const [isDrawingLocked, setIsDrawingLocked] = useState(false);
  const [rawFileData, setRawFileData] = useState([]);
  // Find the numeric ID for "Drawing" under Files -> field_code === "Drawing"
  const drawingDocumentOfId = useMemo(() => {
    return (
      options.find(
        (o) => o.ui_name === "Files" && o.field_code === "Drawing"
      )?.id ?? null
    );
  }, [options]);

  // Only keep files whose document_of matches the Drawing id
  const filteredFiles = useMemo(() => {
    if (!Array.isArray(rawFileData)) return [];
    if (!drawingDocumentOfId) return []; // if options not loaded yet
    return rawFileData.filter(
      (f) => String(f.document_of) === String(drawingDocumentOfId)
    );
  }, [rawFileData, drawingDocumentOfId]);

  useEffect(() => {
    dispatch(getDrawingLockStatus(drawing_id)).then(result => {
      if (result?.payload?.locked) {
        setIsDrawingLocked(result.payload.locked);
      }
    });
    dispatch(getProposalFinalStatus(drawing_id)).then(result => {
      if (result?.payload?.isProposalFinalised) {
        setIsProposalFinalised(true);
      }
    });
    dispatch(getDrawingById(drawing_id)).then((result) => {
      if (result.payload) {
        setDrawingData(result.payload);
        dispatch(proposalStatusByDrawingId(result.payload.uniqueId)).then(
          (result) => {
            if (result.payload) {
              setProposalStatus(result.payload.hasData);
            } else {
              setProposalStatus(false);
            }
          }
        );
        setLoading(false);
      }
    });
    dispatch(getOpportunityById(id)).then((result) => {
      if (result.payload) {
        setOpportunityData(result.payload);
      }
    });
    dispatch(getUserForUI()).then((result) => {
      if (result.payload) {
        setUserData(result.payload);
      }
    });
    dispatch(getDdOptionForUI());
    dispatch(getAllFiles(drawing_id)).then((result) => {
      if (result.payload) {
        setRawFileData(result.payload);
      }
    });
  }, []);

  const drawingValidationSchema = Yup.object().shape({
    requested_by: Yup.string().required("please select requested by"),
    date_required: Yup.string().required("please select date required"),
    status: Yup.string().required("please enter opportunity name"),
  });

  const fileValidationSchema = Yup.object().shape({
    document_type: Yup.string().required("please select document type"),
    document_files: Yup.mixed().required("please select document files"),
  });

  const drawingInitialValues = {
    requested_by: drawingData?.requested_by || "",
    drawing_revision: drawingData?.drawing_revision || "",
    date_requested: drawingData?.date_requested || "",
    date_required: drawingData?.date_required || "",
    status: drawingData?.status || "",
    notes: drawingData?.notes || "",
  };

  const fileInitialValues = {
    document_type: "",
    drawing_revision: null,
    document_files: null,
    convert_to_pdf: false,
    is_final: false
  };

  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  const handleDrawingSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    values.updatedBy = JSON.parse(localStorage.getItem("userSession")).id;
    const formData = new FormData();
    Object.keys(values).forEach((fieldName) => {
      formData.append(fieldName, values[fieldName]);
    });

    await dispatch(updateDrawing({ data: formData, id: drawing_id })).then(
      (result) => {
        if (result.payload) {
          navigate(`/opportunity/${id}`);
          toast.success(result.payload.message);
        } else {
          toast.error(result.error.message);
        }
      }
    );
    setSubmitting(false);
  };

  const handleFileSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    values.drawing_id = drawingData.id;
    values.drawing_uniqueId = drawingData.uniqueId;
    values.createdBy = JSON.parse(localStorage.getItem("userSession")).id;
    values.document_of = options.find(
      (option) => option.ui_name === "Files" && option.field_code === "Drawing"
    )?.id || "";

    const formData = new FormData();
    formData.append("drawing_id", values.drawing_id);
    formData.append("drawing_uniqueId", values.drawing_uniqueId);
    formData.append("document_of", values.document_of);
    formData.append("document_type", values.document_type);
    formData.append("drawing_revision", values.drawing_revision);
    formData.append("file_name", values.document_files[0]?.name || "");
    formData.append("createdBy", values.createdBy);
    formData.append("convert_to_pdf", values.convert_to_pdf);
    formData.append("is_final", values.is_final);

    if (values.document_files && values.document_files.length > 0) {
      Array.from(values.document_files).forEach((file) => {
        formData.append("document_files", file);
      });
    }

    await dispatch(addFile({ data: formData, drawing_id })).then((result) => {
      if (result.payload) {
        resetForm();
        dispatch(getAllFiles(drawing_id)).then((result) => {
          if (result.payload) {
            setRawFileData(result.payload);
          }
        });
        toast.success(result.payload.message);
      } else {
        toast.error(result.error.message);
      }
    });
    setSubmitting(false);
  };

  const handleDeleteFile = async (fileId) => {
    await dispatch(deleteFile({ id: fileId })).then((result) => {
      if (result.payload) {
        dispatch(getAllFiles(drawing_id)).then((result) => {
          if (result.payload) {
            setRawFileData(result.payload);
          }
        });
        toast.success(result.payload.message);
      } else {
        toast.error(result.error.message);
      }
    });
  };

  const viewFile = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    }
  };

  const documentTypeOptions = options
    .filter(
      (option) =>
        option.ui_name === "Files" && option.field_name === "Document_type"
    )
    .sort((a, b) => a.sort_field - b.sort_field);

  const documentOfValue = options.find(
    (option) => option.ui_name === "Files" && option.field_code === "Drawing"
  )?.field_code || "Drawing";

  return (
    <>
      <div className="block md:grid grid-cols-3 items-center mb-[30px]">
        <div className="flex gap-[16px] items-center mb-[32px] md:mb-0">
          <Link to={`/opportunity/${id}`}>
            <div className="bg-[#F8F8F8] rounded-[10px] p-[8px]">
              <img
                className="w-[24px]"
                src={process.env.PUBLIC_URL + "/icons/back.png"}
                alt="back icon"
              />
            </div>
          </Link>
          <div className="flex flex-col">
            <span className="font-Inter font-[400] leading-[16px] text-[#9E9E9E] text-[14px]">
              Drawing / Edit
            </span>
            <span className="font-Inter font-[700] leading-[30px] text-[24px]">
              Drawing
            </span>
          </div>
        </div>
        <div className="font-Inter font-[500] leading-[30px] text-[18px] text-center">
          {opportunityData.id}-{drawing_id}-{opportunityData.opportunity_name}
        </div>
        <Notes type="edit-drawing" drawing_id={drawing_id} />
      </div>
      <DrawingTabs />
      {!loading ? (
        <>
        <div className="block md:flex gap-[20px]">
            <div className="w-full mb-[32px] md:mb-0">
              <Formik
                initialValues={drawingInitialValues}
                validationSchema={drawingValidationSchema}
                onSubmit={handleDrawingSubmit}
              >
                {({ isSubmitting, values, errors, touched, setFieldValue }) => (
                  <Form>
                    <div
                      className={`${
                        activeTab === 1 || activeTab === 2 || activeTab === 3 ? "" : "hidden"
                      } w-full bg-[#F8F8F8] rounded-[12px] p-6 overflow-x-auto`}
                    >
                      {/* ================= SINGLE ROW ================= */}
                      <div className="flex flex-nowrap gap-[20px] w-full items-end">

                        {/* Requested By */}
                        <div className="mb-2 w-[220px] min-w-[220px]">
                          <label className="font-Inter text-[15px] text-[#9E9E9E]">
                            Requested By
                          </label>
                          <Field
                            as="select"
                            name="requested_by"
                            disabled
                            className="block w-full bg-white border border-gray-200 py-3 px-4 rounded-[12px]"
                          >
                            <option value="">Select User</option>
                            {userData.map((user) => (
                              <option value={user.id} key={user.id}>
                                {user.firstname} {user.lastname}
                              </option>
                            ))}
                          </Field>
                        </div>

                        {/* Date Requested */}
                        <div className="mb-2 w-[220px] min-w-[220px]">
                          <label className="font-Inter text-[15px] text-[#9E9E9E]">
                            Date Requested
                          </label>
                          <Field
                            type="date"
                            name="date_requested"
                            className="block w-full bg-white border border-gray-200 py-3 px-4 rounded-[12px]"
                          />
                        </div>

                        {/* Date Required */}
                        <div className="mb-2 w-[220px] min-w-[220px]">
                          <label className="font-Inter text-[15px] text-[#9E9E9E]">
                            Date Required
                          </label>
                          <Field
                            type="date"
                            name="date_required"
                            className="block w-full bg-white border border-gray-200 py-3 px-4 rounded-[12px]"
                          />
                        </div>

                        {/* Status */}
                        <div className="mb-2 w-[220px] min-w-[220px]">
                          <label className="font-Inter text-[15px] text-[#9E9E9E]">
                            Status
                          </label>
                          <Field
                            as="select"
                            name="status"
                            className="block w-full bg-white border border-gray-200 py-3 px-4 rounded-[12px]"
                          >
                            <option value="">Select Status</option>
                            {options
                              .filter(
                                (o) => o.ui_name === "Drawing" && o.field_name === "Status"
                              )
                              .sort((a, b) => a.sort_field - b.sort_field)
                              .map((o) => (
                                <option key={o.id} value={o.id}>
                                  {o.field_code}
                                </option>
                              ))}
                          </Field>
                        </div>

                        {/* NOTES — WIDER */}
                        <div className="mb-2 min-w-[400px] flex-1">
                          <label className="font-Inter text-[15px] text-[#9E9E9E]">
                            Notes
                          </label>
                          <Field
                            as="textarea"
                            name="notes"
                            className="block w-full bg-white border border-gray-200 py-3 px-4 rounded-[12px] h-[52px]"
                          />
                        </div>

                        {/* SAVE BUTTON */}
                        {!isProposalFinalised && !isDrawingLocked && (
                          <div className="mb-2 min-w-[140px]">
                            <button
                              type="submit"
                              className={`w-full py-[14px] rounded-[12px] text-white text-[14px] ${
                                isSubmitting || proposalStatus
                                  ? "bg-gray-300"
                                  : "bg-[#1C1C20]"
                              }`}
                              disabled={isSubmitting || proposalStatus}
                            >
                              {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : "Save"}
                            </button>
                          </div>
                        )}

                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </>

      ) : (
        <div className="text-center">
          <FontAwesomeIcon
            icon={faSpinner}
            size="2xl"
            style={{ color: "#55A14A" }}
            spin
          />
        </div>
      )}
      <div className="mt-8">
        <NotesTable type="edit-drawing" drawing_id={drawing_id} />
      </div>

      <div className="w-full mt-6">
        <div className="bg-[#F8F8F8] rounded-[12px] p-6">
          
          <div className="md:flex justify-between items-center p-[12px]">
            <div className="leading-[30px] font-Mulish font-[700] text-[20px] mb-[8px] md:mb-0">
              <span>Files</span>
            </div>
          </div>

          {!isProposalFinalised && !isDrawingLocked && (
            <Formik
              initialValues={fileInitialValues}
              validationSchema={fileValidationSchema}
              onSubmit={handleFileSubmit}
            >
              {({ isSubmitting, values, errors, touched, setFieldValue }) => (
                <Form>
                  <div className="flex flex-row items-center gap-[20px] mb-4">
                    <div className="flex-1">
                      <label
                        className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                        htmlFor="document_of"
                      >
                        Document Of
                      </label>
                      <input
                        className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        type="text"
                        value={documentOfValue}
                        disabled
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                        htmlFor="document_type"
                      >
                        Document Type
                      </label>
                      <div className="relative">
                        <Field
                          as="select"
                          className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                          id="document_type"
                          name="document_type"
                          onChange={(e) => {
                            setFieldValue("document_type", e.target.value);
                          }}
                        >
                          <option value="">Select Type</option>
                          {documentTypeOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.field_code}
                            </option>
                          ))}
                        </Field>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg
                            className="fill-current h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                          </svg>
                        </div>
                      </div>
                      {errors.document_type && touched.document_type ? (
                        <div className="text-red-700">{errors.document_type}</div>
                      ) : null}
                    </div>
                    { values.document_type == "460" && (
                        <div className="flex gap-4 items-end">
                          <div className="flex-1">
                            <label
                              className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                              htmlFor="drawing_revision"
                            >
                              Drawing Revision
                            </label>
                            <Field
                              className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px]"
                              id="drawing_revision"
                              name="drawing_revision"
                              onChange={(e) =>
                                setFieldValue("drawing_revision", e.target.value)
                              }
                            />
                          </div>
                        </div>

                    )}
                    <div className="flex-1">
                      <label
                        className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                        htmlFor="document_files"
                      >
                        Select Files
                      </label>
                      <input
                        className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        type="file"
                        name="document_files"
                        id="document_files"
                        multiple
                        onChange={(e) => {
                          setFieldValue("document_files", e.currentTarget.files);
                        }}
                      />
                      {errors.document_files && touched.document_files ? (
                        <div className="text-red-700">{errors.document_files}</div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-6">
                      <label
                        className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px] flex items-center"
                        htmlFor="convert_to_pdf"
                      >
                        <Field
                          type="checkbox"
                          name="convert_to_pdf"
                          id="convert_to_pdf"
                          className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded-lg focus:ring-blue-500 focus:ring-2 mr-2"
                          onChange={(e) =>
                            setFieldValue("convert_to_pdf", e.target.checked)
                          }
                        />
                        Convert to PDF
                      </label>
                      {values.document_type == "460" && (
                        <label
                          className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px] flex items-center"
                          htmlFor="is_final"
                        >
                          <Field
                            type="checkbox"
                            name="is_final"
                            id="is_final"
                            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded-lg focus:ring-blue-500 focus:ring-2 mr-2"
                            onChange={(e) => {
                              setFieldValue("is_final", e.target.checked == true)
                            }
                            }
                          />
                          Is Final
                        </label>
                      )}
                    </div>

                    <div className="flex items-center">
                      <button
                        type="submit"
                        name="submit"
                        className={`py-3 px-6 text-center text-white rounded-[12px] text-[15px] ${
                          isSubmitting ? "bg-gray-300" : "bg-[#1C1C20]"
                        }`}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          "Upload File"
                        )}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}

          {/* Files table (always visible) */}
          <div className="p-5 bg-[#F8F8F8] rounded-[12px]">
            <div className="flex flex-col">
              <div className="sm:rounded-lg mt-5">
                <table className="w-full">
                  <thead className="bg-white">
                    <tr className="rounded-[12px]">
                      <th className="px-3 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">Document Of</th>
                      <th className="px-3 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">Document Type</th>
                      <th className="px-3 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">Document Name</th>
                      <th className="px-3 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">Drawing Revision</th>
                      <th className="px-3 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">Is Final</th>
                      <th className="px-3 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">Uploaded By</th>
                      <th className="px-3 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">Uploaded On</th>
                      <th className="px-3 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F] leading-[20px] tracking-wider">Options</th>
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {filteredFiles.length > 0 ? (
                      filteredFiles.map((file, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F] font-[600]">
                            {file.document_of_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F]">
                            {file.document_type_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F]">
                            {file.file_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F]">
                            {((file.drawing_revision == null || file.drawing_revision == "null" || file.drawing_revision == "") ? "-" : file.drawing_revision)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F]">
                            {((file.is_final_drawing_doc) ? "Yes" : "No")}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F]">
                            {file.uploaded_by}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-[14px] text-[#6D6D6F]">
                            {file.createdAt}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right font-medium">
                            <div className="flex gap-[10px] items-center justify-center">
                              <span className="cursor-pointer w-[40px] bg-blue-100 rounded-lg p-2">
                                <img
                                  src={process.env.PUBLIC_URL + "/icons/viewfile.png"}
                                  width={30}
                                  onClick={() => viewFile(file.document_name)}
                                  alt="View File"
                                />
                              </span>
                              {!isProposalFinalised && !isDrawingLocked && (
                                <span className="cursor-pointer">
                                  <img
                                    src={process.env.PUBLIC_URL + "/icons/delete.png"}
                                    alt="Delete"
                                    className="w-[40px] bg-red-100 rounded-lg"
                                    onClick={() => handleDeleteFile(file.id)}
                                  />
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-6 py-4 text-center text-[#6D6D6F]">
                          No files uploaded
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}

export default EditDrawing;